/**
 * S&P municipal bond use-of-proceeds taxonomy for Advanced Search → Sector.
 * Codes match the backend `UseOfProceedsType` enum names so the selected array
 * can be sent directly as `useOfProceeds: string[]` (backend deserializes by name).
 */

export const BOND_SECTOR_CODES = [
  "AcuteCare",
  "Agriculture",
  "Airlines",
  "Airport",
  "AssistedLiving",
  "ZoosBeaches",
  "Bridges",
  "Building",
  "Transportation",
  "CharterSchool",
  "CivicConventionCenters",
  "CombinedUtilities",
  "CorrectionFacilities",
  "CorrectionFacilitiesCourts",
  "Courts",
  "EconomicDevelopment",
  "Education",
  "ElectricAndPublicPower",
  "Electricity",
  "EnvironmentProtection",
  "Equipment",
  "FireStationAndEquipment",
  "FloodControlStormDrain",
  "Gas",
  "GeneralPurpose",
  "GovernmentPublicBuildings",
  "SeaportsMarineTerminals",
  "HealthcareSystem",
  "HigherEducation",
  "Highways",
  "HospitalEquipmentLoans",
  "Hospitals",
  "Housing",
  "IndustrialDevelopment",
  "Irrigation",
  "LandPreservation",
  "Libraries",
  "LibrariesMuseums",
  "MallsShoppingCenters",
  "MassRapidTransport",
  "MultiFamilyHousing",
  "Museums",
  "NurseHomes",
  "OfficeBuilding",
  "OtherEducation",
  "OtherHealthcare",
  "OtherHousing",
  "OtherPublicService",
  "OtherRecreation",
  "OtherTransportation",
  "OtherUtilities",
  "ParkingFacilities",
  "Parks",
  "ParksZoosBeaches",
  "PensionFundingRetirement",
  "PoliceStationEquipment",
  "PollutionControl",
  "PrimaryAndSecondaryEducation",
  "Psychiatric",
  "PublicImprovements",
  "PublicPower",
  "PublicSafety",
  "Rail",
  "Recreation",
  "RedevelopmentLandClearance",
  "Rehabilitation",
  "ResourceRecovery",
  "RetirementCenters",
  "RoadHighway",
  "Roads",
  "Sanitation",
  "SeniorHousing",
  "Sewer",
  "SingleFamilyHousing",
  "SingleAndMultiFamilyHousing",
  "SolidWaste",
  "SolidWasteResourceRecovery",
  "StadiumsSportsComplex",
  "StudentLoans",
  "SwimmingPool",
  "TeleCommunication",
  "Theaters",
  "Tobacco",
  "TollRoadsHighwaysStreets",
  "TrustReceiptsPortfolio",
  "Tunnels",
  "Turnpike",
  "VariousPurpose",
  "Veterans",
  "Water",
  "WaterAndSewer",
] as const;

export type BondSectorCode = (typeof BOND_SECTOR_CODES)[number];

export interface BondSectorOption {
  code: BondSectorCode;
  label: string;
}

export interface SectorGroup {
  group: string;
  options: readonly BondSectorOption[];
}

export const SECTOR_GROUPS: readonly SectorGroup[] = [
  {
    group: "Education",
    options: [
      { code: "PrimaryAndSecondaryEducation", label: "Primary & Secondary Education" },
      { code: "CharterSchool",                label: "Charter School" },
      { code: "HigherEducation",              label: "Higher Education" },
      { code: "OtherEducation",               label: "Other Education" },
      { code: "Education",                    label: "Education" },
      { code: "StudentLoans",                 label: "Student Loans" },
      { code: "Libraries",                    label: "Libraries" },
      { code: "LibrariesMuseums",             label: "Libraries/Museums" },
      { code: "Museums",                      label: "Museums" },
    ],
  },
  {
    group: "Healthcare",
    options: [
      { code: "Hospitals",             label: "Hospitals" },
      { code: "AcuteCare",             label: "Acute Care" },
      { code: "HospitalEquipmentLoans", label: "Hospital Equipment Loans" },
      { code: "HealthcareSystem",      label: "Healthcare System" },
      { code: "NurseHomes",            label: "Nurse Homes" },
      { code: "Psychiatric",           label: "Psychiatric" },
      { code: "Rehabilitation",        label: "Rehabilitation" },
      { code: "AssistedLiving",        label: "Assisted Living" },
      { code: "RetirementCenters",     label: "Retirement Centers" },
      { code: "SeniorHousing",         label: "Senior Housing" },
      { code: "OtherHealthcare",       label: "Other Healthcare" },
      { code: "Veterans",              label: "Veterans" },
    ],
  },
  {
    group: "Housing",
    options: [
      { code: "SingleFamilyHousing",          label: "Single Family Housing" },
      { code: "MultiFamilyHousing",           label: "Multi-Family Housing" },
      { code: "SingleAndMultiFamilyHousing",  label: "Single and Multi-Family Housing" },
      { code: "OtherHousing",                 label: "Other Housing" },
      { code: "Housing",                      label: "Housing" },
    ],
  },
  {
    group: "Transportation",
    options: [
      { code: "Airport",                  label: "Airport" },
      { code: "Airlines",                 label: "Airlines" },
      { code: "Highways",                 label: "Highways" },
      { code: "RoadHighway",              label: "Road/Highway" },
      { code: "Roads",                    label: "Roads" },
      { code: "TollRoadsHighwaysStreets", label: "Toll Roads/Highways/Streets" },
      { code: "Turnpike",                 label: "Turnpike" },
      { code: "Bridges",                  label: "Bridges" },
      { code: "Tunnels",                  label: "Tunnels" },
      { code: "Rail",                     label: "Rail" },
      { code: "MassRapidTransport",       label: "Mass/Rapid Transport" },
      { code: "ParkingFacilities",        label: "Parking Facilities" },
      { code: "SeaportsMarineTerminals",  label: "Seaports/Marine Terminals" },
      { code: "OtherTransportation",      label: "Other Transportation" },
      { code: "Transportation",           label: "Transportation" },
    ],
  },
  {
    group: "Utilities",
    options: [
      { code: "Water",               label: "Water" },
      { code: "Sewer",               label: "Sewer" },
      { code: "WaterAndSewer",       label: "Water & Sewer" },
      { code: "ElectricAndPublicPower", label: "Electric & Public Power" },
      { code: "Electricity",         label: "Electricity" },
      { code: "PublicPower",         label: "Public Power" },
      { code: "Gas",                 label: "Gas" },
      { code: "CombinedUtilities",   label: "Combined Utilities" },
      { code: "OtherUtilities",      label: "Other Utilities" },
    ],
  },
  {
    group: "Environment & Natural Resources",
    options: [
      { code: "SolidWaste",                  label: "Solid Waste" },
      { code: "SolidWasteResourceRecovery",  label: "Solid Waste/Resource Recovery" },
      { code: "ResourceRecovery",            label: "Resource Recovery" },
      { code: "PollutionControl",            label: "Pollution Control" },
      { code: "EnvironmentProtection",       label: "Environment Protection" },
      { code: "FloodControlStormDrain",      label: "Flood Control/Storm Drain" },
      { code: "Sanitation",                  label: "Sanitation" },
      { code: "Irrigation",                  label: "Irrigation" },
      { code: "LandPreservation",            label: "Land Preservation" },
    ],
  },
  {
    group: "Public Safety & Government Facilities",
    options: [
      { code: "CorrectionFacilities",        label: "Correction Facilities" },
      { code: "CorrectionFacilitiesCourts",  label: "Correction Facilities/Courts" },
      { code: "Courts",                      label: "Courts" },
      { code: "PoliceStationEquipment",      label: "Police Station/Equipment" },
      { code: "FireStationAndEquipment",     label: "Fire Station & Equipment" },
      { code: "PublicSafety",                label: "Public Safety" },
      { code: "GovernmentPublicBuildings",   label: "Government/Public Buildings" },
      { code: "CivicConventionCenters",      label: "Civic/Convention Centers" },
    ],
  },
  {
    group: "Economic & Industrial Development",
    options: [
      { code: "IndustrialDevelopment",      label: "Industrial Development" },
      { code: "EconomicDevelopment",        label: "Economic Development" },
      { code: "RedevelopmentLandClearance", label: "Redevelopment/Land Clearance" },
      { code: "Tobacco",                    label: "Tobacco" },
      { code: "TeleCommunication",          label: "TeleCommunication" },
      { code: "Agriculture",                label: "Agriculture" },
      { code: "Equipment",                  label: "Equipment" },
    ],
  },
  {
    group: "Recreation & Culture",
    options: [
      { code: "Parks",                  label: "Parks" },
      { code: "ParksZoosBeaches",       label: "Parks/Zoos/Beaches" },
      { code: "ZoosBeaches",            label: "Zoos/Beaches" },
      { code: "StadiumsSportsComplex",  label: "Stadiums/Sports Complex" },
      { code: "SwimmingPool",           label: "Swimming Pool" },
      { code: "OtherRecreation",        label: "Other Recreation" },
      { code: "Recreation",             label: "Recreation" },
      { code: "Theaters",               label: "Theaters" },
      { code: "MallsShoppingCenters",   label: "Malls/Shopping Centers" },
      { code: "OfficeBuilding",         label: "Office Building" },
    ],
  },
  {
    group: "Public Improvements & General Government",
    options: [
      { code: "GeneralPurpose",           label: "General Purpose" },
      { code: "VariousPurpose",           label: "Various Purpose" },
      { code: "PublicImprovements",       label: "Public Improvements" },
      { code: "OtherPublicService",       label: "Other Public Service" },
      { code: "PensionFundingRetirement", label: "Pension Funding/Retirement" },
      { code: "Building",                 label: "Building" },
    ],
  },
  {
    group: "Miscellaneous / Structured",
    options: [
      { code: "TrustReceiptsPortfolio", label: "Trust Receipts - Portfolio" },
    ],
  },
];

/** Flat list of all sector options, ordered by group then position within group. */
export const BOND_SECTOR_OPTIONS: readonly BondSectorOption[] =
  SECTOR_GROUPS.flatMap((g) => g.options);

const CODE_SET = new Set<string>(BOND_SECTOR_OPTIONS.map((o) => o.code));

export function isBondSectorCode(value: string): value is BondSectorCode {
  return CODE_SET.has(value);
}

const LABEL_BY_CODE = Object.fromEntries(
  BOND_SECTOR_OPTIONS.map((o) => [o.code, o.label]),
) as Record<BondSectorCode, string>;

export function bondSectorLabel(code: BondSectorCode): string {
  return LABEL_BY_CODE[code];
}
